import type { FormLocation } from '../utils/trackingUtils';
// Recruitment Email Service
// Handles sending recruitment form emails via Brevo API

import { emailConfig } from '../config/emailConfig';
import { processEmailTemplate } from './emailTemplateService';
import { fileToAttachment } from '../utils/fileUtils';
import { LOGO_URL } from './logoConstants';

export interface EmailResult {
    success: boolean;
    messageId?: string;
    error?: string;
}

export interface RecruitmentFormData {
    name: string;
    surname: string;
    email: string;
    phone: string;
    currentSituation: string;
    position: string;
    message?: string;
    cvFile?: File; // Used for upload
    cvDownloadUrl?: string; // Download URL after upload (for email template link)
    cvFileName?: string; // Original filename
    form_location?: FormLocation;
}

// Position mapping
const positionMap: Record<string, string> = {
    'contabilidade-administrativo': 'Contabilidade/Administrativo(a)',
    'vendas': 'Vendas',
    'rececionista': 'Rececionista',
    'mecanico-auto': 'Mecânico(a) Auto',
    'aprendiz-mecanico': 'Aprendiz de Mecânico',
    'lavagem-auto': 'Lavagem Auto'
};

// Current situation mapping
const currentSituationMap: Record<string, string> = {
    'student': 'Estudante',
    'unemployed': 'Desempregado',
    'employed': 'Empregado'
};

/**
 * Get position name in Portuguese
 */
function getPositionName(position: string): string {
    return positionMap[position] || position || 'Não especificado';
}

/**
 * Get current situation name in Portuguese
 */
function getCurrentSituationName(situation: string): string {
    return currentSituationMap[situation] || situation || 'Não especificado';
}

// Single email interface for batch sending
interface SingleEmailPayload {
    to: { email: string; name?: string };
    subject: string;
    htmlContent: string;
    sender?: { name: string; email: string };
    attachments?: Array<{ name: string; content: string }>;
}

// Batch email result
interface BatchEmailResult {
    success: boolean;
    results?: Array<{ success: boolean; messageId?: string; error?: string }>;
    summary?: { total: number; sent: number; failed: number };
    error?: string;
}

/**
 * Get reCAPTCHA token from the page
 */
function getRecaptchaToken(): string {
    try {
        if (typeof (window as any).grecaptcha !== 'undefined') {
            const token = (window as any).grecaptcha.getResponse();
            if (token) {
                return token;
            }
        }
        
        // Fallback: check hidden textarea
        const textarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
        if (textarea && textarea.value) {
            return textarea.value;
        }
    } catch (error) {
    }
    
    return '';
}

/**
 * Send batch emails via Firebase Cloud Function
 * This sends multiple emails in a single request, verifying reCAPTCHA only once
 */
async function sendBatchEmailsViaBrevo(
    emails: SingleEmailPayload[],
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        const token = recaptchaToken || getRecaptchaToken();
        if (!token) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const emailsWithSender = emails.map(email => ({
            ...email,
            sender: email.sender || {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            }
        }));

        // Log attachment info for debugging
        const payload = {
            emails: emailsWithSender,
            recaptchaToken: token
        };


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { success: true };
        }


        return {
            success: result.success,
            results: result.results,
            summary: result.summary
        };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send email via Firebase Cloud Function
 * The Cloud Function handles Brevo API credentials securely
 */
async function sendEmailViaBrevo(
    to: { email: string; name?: string },
    subject: string,
    htmlContent: string,
    attachments?: Array<{ name: string; content: string }>
): Promise<EmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        // Get reCAPTCHA token
        const recaptchaToken = getRecaptchaToken();
        if (!recaptchaToken) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const payload: any = {
            sender: {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            },
            to,
            subject,
            htmlContent,
            recaptchaToken
        };

        // Add attachments if provided
        if (attachments && attachments.length > 0) {
            payload.attachments = attachments;
        }


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { messageId: 'sent' };
        }

        return { success: true, messageId: result.messageId };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send recruitment notification email to admin
 */
export async function sendRecruitmentAdminEmail(
    formData: RecruitmentFormData,
    companyName: string = 'Roady Leiria'
): Promise<EmailResult> {
    try {

        const position = getPositionName(formData.position);
        const situation = getCurrentSituationName(formData.currentSituation);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            position: position,
            current_situation: situation,
            message: formData.message || '',
            cv_file: formData.cvFile?.name || '',
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            message_style: formData.message ? '' : 'display: none;',
            cv_style: formData.cvFile ? '' : 'display: none;'
        };

        // Process admin template
        const htmlContent = await processEmailTemplate('recruitment-admin.html', variables);

        const subject = `Nova Candidatura para ${position}`;

        // Convert CV file to base64 attachment
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.cvFile) {
            attachments = await fileToAttachment(formData.cvFile);
        }

        const result = await sendEmailViaBrevo(
            {
                email: emailConfig.BREVO_TO_EMAIL_RECRUITMENT,
                name: `${companyName} Admin`
            },
            subject,
            htmlContent,
            attachments
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

// Get company address helper
function getCompanyAddress(name: string): string {
    if (name.includes('Valongo')) return 'Tv. António Augusto Castro Paupério 36, Valongo';
    if (name.includes('Leiria')) return 'Rua da Baquelite 204, Leiria';
    if (name.includes('Oliveira')) return 'Av. D. Maria I - Lugar de Aldas, 3720-300 Oliveira de Azeméis';
    if (name.includes('São Domingos')) return 'Avenida Antero Brotas – Sítio do Esteval, 2870-122 São Domingos de Rana';
    if (name.includes('Montijo')) return 'Av. dos Pescadores 18, 2870-355 Montijo';
    return 'Portugal';
}

/**
 * Prepare recruitment admin email payload (for batch sending)
 */
async function prepareRecruitmentAdminEmail(
    formData: RecruitmentFormData,
    companyName: string = 'Roady'
): Promise<SingleEmailPayload | null> {
    try {
        const position = getPositionName(formData.position);
        const situation = getCurrentSituationName(formData.currentSituation);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // Include CV download URL in template variables (as a clickable link)
        const cvLink = formData.cvDownloadUrl 
            ? `<a href="${formData.cvDownloadUrl}" target="_blank" style="color: #d90202; text-decoration: underline; font-weight: bold;">${formData.cvFileName || 'Download CV'}</a>`
            : '';

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            position: position,
            current_situation: situation,
            message: formData.message || '',
            cv_file: formData.cvFileName || formData.cvFile?.name || '',
            cv_download_link: cvLink, // Download URL as clickable link
            cv_download_url: formData.cvDownloadUrl || '', // Raw URL if needed
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            message_style: formData.message ? '' : 'display: none;',
            cv_style: (formData.cvDownloadUrl || formData.cvFile) ? '' : 'display: none;'
        };

        const htmlContent = await processEmailTemplate('recruitment-admin.html', variables);
        const subject = `Nova Candidatura para ${position}`;

        // CV is sent as a real attachment; the Storage download link in the template is kept as fallback
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.cvFile) {
            attachments = await fileToAttachment(formData.cvFile);
        }

        const emailPayload: SingleEmailPayload = {
            to: { email: emailConfig.BREVO_TO_EMAIL_RECRUITMENT, name: `${companyName} Admin` },
            subject,
            htmlContent,
            attachments: attachments.length > 0 ? attachments : undefined
        };

        return emailPayload;
    } catch (error: any) {
        return null;
    }
}

/**
 * Prepare recruitment user email payload (for batch sending)
 */
async function prepareRecruitmentUserEmail(
    formData: RecruitmentFormData,
    companyName: string = 'Roady',
    language: string = 'pt'
): Promise<SingleEmailPayload | null> {
    try {
        if (!formData.email) {
            return null;
        }

        const position = getPositionName(formData.position);

        const cvLink = formData.cvDownloadUrl
            ? `<a href="${formData.cvDownloadUrl}" target="_blank" style="color: #d90202; text-decoration: underline; font-weight: bold;">${formData.cvFileName || 'Download CV'}</a>`
            : '';

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            position: position,
            year: new Date().getFullYear().toString(),
            logo_url: LOGO_URL,
            recruitment_email: emailConfig.BREVO_TO_EMAIL_RECRUITMENT || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName),
            cv_download_link: cvLink,
            cv_style: formData.cvDownloadUrl ? '' : 'display: none;'
        };

        const templateName = language === 'en'
            ? 'recruitment-user-en.html'
            : 'recruitment-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Application Confirmation - ${companyName}`
            : `Confirmação de Candidatura - ${companyName}`;

        // Include the CV attachment on the applicant's confirmation as well
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.cvFile) {
            attachments = await fileToAttachment(formData.cvFile);
        }

        return {
            to: { email: formData.email, name: `${formData.name} ${formData.surname}` },
            subject,
            htmlContent,
            attachments: attachments.length > 0 ? attachments : undefined
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Send both recruitment emails in a single batch (admin + user confirmation)
 */
export async function sendRecruitmentEmailsBatch(
    formData: RecruitmentFormData,
    companyName: string = 'Roady',
    language: string = 'pt',
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {

        const emails: SingleEmailPayload[] = [];

        // Prepare admin email
        const adminEmail = await prepareRecruitmentAdminEmail(formData, companyName);
        if (adminEmail) {
            emails.push(adminEmail);
        }

        // Prepare user confirmation email
        const userEmail = await prepareRecruitmentUserEmail(formData, companyName, language);
        if (userEmail) {
            emails.push(userEmail);
        }

        if (emails.length === 0) {
            throw new Error('No emails to send');
        }

        const result = await sendBatchEmailsViaBrevo(emails, recaptchaToken);

        if (result.success) {
        } else {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send recruitment confirmation email to user
 */
export async function sendRecruitmentUserEmail(
    formData: RecruitmentFormData,
    companyName: string = 'Roady Leiria',
    language: string = 'pt'
): Promise<EmailResult> {
    try {
        if (!formData.email) {
            return { success: true }; // No email provided, skip
        }


        const position = getPositionName(formData.position);

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            position: position,
            year: new Date().getFullYear().toString(),
            logo_url: LOGO_URL,
            recruitment_email: emailConfig.BREVO_TO_EMAIL_RECRUITMENT || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName)
        };

        // Process user template based on language
        const templateName = language === 'en' 
            ? 'recruitment-user-en.html' 
            : 'recruitment-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Application Confirmation - ${companyName}`
            : `Confirmação de Candidatura - ${companyName}`;

        const result = await sendEmailViaBrevo(
            {
                email: formData.email,
                name: `${formData.name} ${formData.surname}`
            },
            subject,
            htmlContent
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

